import { doc, getDoc } from 'firebase/firestore';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import BackButton from '../../components/BackButton/BackButton';
import { db } from '../../lib/firebase';
import Styles from './Licensing.module.css';

// Fallback promo code applied when no referral code is entered, or when
// the entered code doesn't match a document in the "referralCode" collection.
const DEFAULT_PROMO_CODE = 'BRASS75';
const DEFAULT_PRICE_LABEL = '$4.99';
const REFERRAL_PRICE_LABEL = '$0.99';

const FEATURES = [
  {
    title: 'All 5 Brass Instruments',
    desc: 'Trumpet, trombone, French horn, baritone, and tuba — all included under one license.',
  },
  {
    title: 'Unlimited Students',
    desc: 'One school license covers your entire brass program. No per-seat fees, ever.',
  },
  {
    title: 'Game-Based Learning',
    desc: 'Students actually want to practice. Engaging drills that build real technique.',
  },
];

const Licensing = () => {
  const [form, setForm] = useState({
    firstname: '',
    lastname: '',
    contactEmail: '',
    schoolDistrict: '',
    referralCode: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 'idle' | 'checking' | 'valid' | 'invalid'
  const [referralStatus, setReferralStatus] = useState('idle');
  const [referralPromoCode, setReferralPromoCode] = useState(null);
  const debounceRef = useRef(null);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // Check the referral code against the "referralCode" Firestore collection
  // shortly after the user stops typing.
  useEffect(() => {
    const code = form.referralCode.trim();

    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!code) {
      setReferralStatus('idle');
      setReferralPromoCode(null);
      return;
    }

    setReferralStatus('checking');

    debounceRef.current = setTimeout(async () => {
      try {
        const result = await validateReferralCode(code);
        if (result.valid) {
          setReferralStatus('valid');
          setReferralPromoCode(result.promoCode || null);
        } else {
          setReferralStatus('invalid');
          setReferralPromoCode(null);
        }
      } catch (error) {
        console.error('Error validating referral code:', error);
        setReferralStatus('invalid');
        setReferralPromoCode(null);
      }
    }, 500);

    return () => clearTimeout(debounceRef.current);
  }, [form.referralCode]);

const validateReferralCode = async (code) => {
    try {
      const formattedCode = code.trim().toUpperCase();

      const referralDocRef = doc(db, 'referralCodes', formattedCode);
      const referralDoc = await getDoc(referralDocRef);

      if (!referralDoc.exists()) {
        return { valid: false };
      }

      const data = referralDoc.data();

      if (data && data.active === false) {
        return { valid: false };
      }

      return {
        valid: true,
        promoCode: 'REFERRAL99',
      };
    } catch (err) {
      console.error('💥 Firebase fetch error:', err);
      throw err;
    }
  };

  const isReferralValid = referralStatus === 'valid';
  const displayPrice = isReferralValid ? REFERRAL_PRICE_LABEL : DEFAULT_PRICE_LABEL;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.firstname || !form.lastname || !form.contactEmail || !form.schoolDistrict) {
      toast.error('Please fill out all required fields before purchasing', {
        position: 'top-right',
        autoClose: 3000,
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const formData = new URLSearchParams();
      formData.append('firstname', form.firstname);
      formData.append('lastname', form.lastname);
      formData.append('contactEmail', form.contactEmail);
      formData.append('schoolDistrict', form.schoolDistrict);
      formData.append('referralCode', form.referralCode);

      await fetch(
        'https://script.google.com/macros/s/AKfycbzbz_vTVwu1Lws9L6_OEmMBQk0vpdKBmRkaZ4zfdN_GYXsoYO7T3DVJ-UrAYPBOFu6N/exec',
        {
          method: 'POST',
          body: formData,
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        },
      );

      // Figure out which promo to apply. If the debounced check already
      // came back, trust it; otherwise (e.g. user submitted fast) do one
      // last check before deciding.
      let promoCode = DEFAULT_PROMO_CODE;

      const trimmedCode = form.referralCode.trim();
      if (trimmedCode) {
        if (referralStatus === 'valid' && referralPromoCode) {
          promoCode = referralPromoCode;
        } else if (referralStatus === 'checking' || referralStatus === 'idle') {
          try {
            const result = await validateReferralCode(trimmedCode);
            if (result.valid) {
              promoCode = result.promoCode || 'REFERRAL99';
            }
          } catch (error) {
            console.error('Referral re-check before checkout failed:', error);
            // Fall back to the default promo rather than blocking checkout.
          }
        }
      }

      // NOTE: this is your Stripe subscription payment link — the
      // prefilled_promo_code param drives which discount gets applied.
      window.location.href = `https://buy.stripe.com/7sY00i8aIa1X7vabBd6Vq04?prefilled_promo_code=${promoCode}`;
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Something went wrong. Please try again.', {
        position: 'top-right',
        autoClose: 3000,
      });
      setIsSubmitting(false);
    }
  };

  return (
    <div className={Styles.page}>
      <div className={Styles.backWrap}>
        <BackButton />
      </div>

      <div className={Styles.hero}>
        {/* Left — value prop */}
        <div className={Styles.left}>
          <p className={Styles.eyebrow}>School Licensing</p>
          <h1 className={Styles.headline}>
            The brass learning platform your students will love.
          </h1>
          <p className={Styles.subhead}>
            Every instrument. Every student. No limits.
          </p>

          <div className={Styles.priceBlock}>
            <div className={Styles.promoBadge}>
              {isReferralValid ? 'REFERRAL DISCOUNT APPLIED' : '75% OFF FIRST MONTH'}
            </div>
            <div className={Styles.priceRow}>
              <span className={Styles.price}>{displayPrice}</span>
              <span className={Styles.pricePer}>&nbsp;/ first month</span>
            </div>
            <p className={Styles.priceSubtext}>Then $19.99/month. Cancel anytime.</p>
          </div>

          <ul className={Styles.features}>
            {FEATURES.map((f) => (
              <li key={f.title} className={Styles.feature}>
                <div className={Styles.dot} />
                <div>
                  <p className={Styles.featureTitle}>{f.title}</p>
                  <p className={Styles.featureDesc}>{f.desc}</p>
                </div>
              </li>
            ))}
          </ul>

          <div className={Styles.trust}>
            <span>Secure checkout via Stripe</span>
            <span>PO & invoice available</span>
            <span>W-9 on request</span>
          </div>
        </div>

        {/* Right — form */}
        <div className={Styles.right}>
          <div className={Styles.card}>
            <h2 className={Styles.cardTitle}>Get Started Today</h2>
            <p className={Styles.cardSub}>
              Fill in your details and you'll be taken directly to checkout.
            </p>

            <form onSubmit={handleSubmit} className={Styles.form}>
              <div className={Styles.row}>
                <div className={Styles.field}>
                  <label htmlFor="firstname">First Name</label>
                  <input
                    id="firstname"
                    type="text"
                    name="firstname"
                    value={form.firstname}
                    onChange={handleChange}
                    placeholder="Jane"
                    required
                  />
                </div>
                <div className={Styles.field}>
                  <label htmlFor="lastname">Last Name</label>
                  <input
                    id="lastname"
                    type="text"
                    name="lastname"
                    value={form.lastname}
                    onChange={handleChange}
                    placeholder="Smith"
                    required
                  />
                </div>
              </div>

              <div className={Styles.field}>
                <label htmlFor="contactEmail">Preferred Email (Personal recommended)</label>
                <input
                  id="contactEmail"
                  type="email"
                  name="contactEmail"
                  value={form.contactEmail}
                  onChange={handleChange}
                  placeholder="jsmith@gmail.edu"
                  required
                />
              </div>

              <div className={Styles.field}>
                <label htmlFor="schoolDistrict">School District</label>
                <input
                  id="schoolDistrict"
                  type="text"
                  name="schoolDistrict"
                  value={form.schoolDistrict}
                  onChange={handleChange}
                  placeholder="Lincoln County Schools"
                  required
                />
              </div>

              <div className={Styles.field}>
                <label htmlFor="referralCode">Referral Code (Optional)</label>
                <input
                  id="referralCode"
                  type="text"
                  name="referralCode"
                  value={form.referralCode}
                  onChange={handleChange}
                  placeholder="SMITH2026"
                />
                {referralStatus === 'checking' && (
                  <p className={`${Styles.referralStatus} ${Styles.referralChecking}`}>
                    Checking code…
                  </p>
                )}
                {referralStatus === 'valid' && (
                  <p className={`${Styles.referralStatus} ${Styles.referralValid}`}>
                    ✓ Code applied — first month is {REFERRAL_PRICE_LABEL}
                  </p>
                )}
                {referralStatus === 'invalid' && (
                  <p className={`${Styles.referralStatus} ${Styles.referralInvalid}`}>
                    Code not recognized — standard pricing applies
                  </p>
                )}
              </div>

              <button
                type="submit"
                className={Styles.submitBtn}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <><span className={Styles.loader} /> Processing…</>
                ) : (
                  `Claim Offer — ${displayPrice}`
                )}
              </button>
            </form>

            <p className={Styles.guarantee}>
              Your information is secure and never shared.
              School code delivered to your inbox within 24 hours.
            </p>
          </div>
        </div>
      </div>

      <footer className={Styles.footer}>
        Questions? Contact us at <strong>support@brassflows.com</strong>
      </footer>
    </div>
  );
};

export default Licensing;